<template>
	<div class="relative h-full">
		<Carousel
			class="size-full"
			:opts="{
				loop: true
			}"
			:plugins="[WheelGesturesPlugin()]"
			@init-api="setupCarouselApi"
		>
			<CarouselContent class="h-full">
				<CarouselItem
					v-for="(image, index) in propertyLimitedImages"
					:key="image.watermarkUrl || image.url"
					@click="handlePropertyClick()"
				>
					<PropertyCardCarouselImage
						:index="index"
						:image="image"
						:variant="variant"
						:property-type="property.propertyType?.name"
						:suites="property.suites"
						:region="property.region"
						:city="property.city"
						:asking-price="property.askingPrice"
					/>
				</CarouselItem>
			</CarouselContent>
			<template v-if="propertyLimitedImages?.length > 1">
				<CarouselPrevious
					:is-mouse-over-carousel="isMouseOverCardHeader"
					:class="{
						'hidden md:block': variant === 'fullFace'
					}"
					@click="trackPropertyImageScrollClick()"
				>
					<IconMaterialSymbolsLightChevronLeft class="size-8 stroke-2" />
				</CarouselPrevious>
				<CarouselNext
					:is-mouse-over-carousel="isMouseOverCardHeader"
					:class="{
						'hidden md:block': variant === 'fullFace'
					}"
					@click="trackPropertyImageScrollClick()"
				>
					<IconMaterialSymbolsLightChevronRight class="size-8 stroke-2" />
				</CarouselNext>
			</template>
		</Carousel>

		<div
			v-if="propertyLimitedImages?.length > 1"
			:class="
				cn(
					propertyCardCarouselDotsVariants({
						size: variant === 'fullFace' ? 'lg' : 'sm'
					}),
					'opacity-100 group-hover/card:opacity-100 md:opacity-0'
				)
			"
		>
			<button
				v-for="(_, index) in propertyLimitedImages"
				:key="`property-carousel-index:${index}`"
				class="size-1 min-w-1 cursor-pointer"
				:class="isCurrentItem(index) ? 'bg-white' : 'bg-white/50'"
				@click="$event => goTo($event, index)"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { cn } from '@/utils';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import type { HTMLAttributes } from 'vue';
import { useEvents } from '~/composables/events/useEvents';
import type { Property } from '~/core/entities/property';
import { MixpanelProps } from '~/core/service/mixpanel';
import type { SectionName } from '~/types/mixpanel';
import { propertyCardCarouselDotsVariants, PropertyCardCarouselImage } from '.';
import {
	CarouselNext,
	CarouselPrevious,
	type CarouselApi
} from '../ui/carousel';

export type PropertyCardHeaderProps = {
	class?: HTMLAttributes['class'];
	isMouseOverCardHeader?: boolean;
	property: Property;
	size?: 'sm' | 'md' | 'lg';
	variant?: 'fullFace' | 'whiteBackground' | 'transparentBackground';
	cardPosition?: number;
	section?: SectionName;
};

const props = defineProps<PropertyCardHeaderProps>();

const { trackPropertyCardClicked, trackPropertyImageScrolled } = useEvents();
const { smaller } = useBreakpoints(breakpointsTailwind);
const route = useRoute();
const carouselApi = ref<CarouselApi | null>(null);

const currentItem = ref<number | undefined>(0);
const clickCounts = ref(0);

const propertyLimitedImages = computed(() => props.property.images.slice(0, 5));
const isBelowMdWidth = computed(() => smaller('md'));

const section = computed<SectionName | ''>(() => {
	if (props.section) {
		return props.section;
	}

	if (route.fullPath.startsWith('/venda/mapa-de-imoveis')) {
		return 'listed properties';
	}

	return '';
});

const trackingSharedProps = computed(() => ({
	[MixpanelProps.COMMERCIAL_ID]: props.property.commercialId,
	[MixpanelProps.CURRENT_PAGE]: route.fullPath,
	[MixpanelProps.SECTION_NAME]: section.value,
	[MixpanelProps.CARD_POSITION]: isNumber(props.cardPosition)
		? props.cardPosition
		: undefined,
	[MixpanelProps.CARD_TYPE]:
		props.variant === 'fullFace' ? 'full face' : 'regular card',
	[MixpanelProps.COMPANY_NAME]: props.property.company.name,
	[MixpanelProps.TAG_EXCLUSIVE]: props.property.isExclusive,
	[MixpanelProps.AGENT_NAME]: props.property.agent.name
}));

function setupCarouselApi(api: CarouselApi) {
	carouselApi.value = api;
	carouselApi.value?.on('select', () => {
		currentItem.value = carouselApi.value?.selectedScrollSnap();
	});
}

function isCurrentItem(index: number) {
	return currentItem.value === index;
}

function goTo(event: MouseEvent, index: number) {
	event.stopPropagation();
	event.preventDefault();
	carouselApi.value?.scrollTo(index);
}

function handlePropertyClick() {
	trackPropertyCardClicked({
		[MixpanelProps.DESTINATION_URL]: `/imovel/${props.property.commercialId}/${props.property.slug}`,
		[MixpanelProps.PHOTO_URL]: isNumber(currentItem.value)
			? props.property.images[currentItem.value]?.watermarkUrl
			: undefined,
		[MixpanelProps.PHOTO_POSITION]: isNumber(currentItem.value)
			? currentItem.value + 1
			: undefined,
		...trackingSharedProps.value
	});
}

function trackPropertyImageScrollClick() {
	clickCounts.value += 1;
	trackPropertyImageScrolled({
		[MixpanelProps.CLICK_COUNT]: clickCounts.value,
		...trackingSharedProps.value
	});
}
</script>
